import React, { useEffect, useMemo, useState } from "react";
import { BiExport, BiFile } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import oDayJs from "dayjs";

import { BUTTON_CONFIG } from "config/constants/Button";
import { Button, ComboBox, DateInput, Table, Text } from "components";

import { actionReportGetTax, selectReportsState } from "../redux";
import { TaxMapper } from "../mappers/ReportMapper";
import { CastDate } from "utils/SharedUtils";
import { DATES_FORMATS } from "config/constants/Dates";
import { CURRENCY_VALUES } from "config/constants/Currency";
import { selectLoguedUser } from "redux/auth/AuthSelector";
import { SELECTABLE_DOCUMENT_TYPES } from "config/constants/Documents";

const csvHeaders = [
  { label: "Tipo", key: "type" },
  { label: "Fecha", key: "date" },
  { label: "Nro Interno", key: "nro" },
  { label: "CAE", key: "cae" },
  { label: "Total IVA", key: "iva" },
  { label: "Estado", key: "status" },
];

const TaxReport = () => {
  const oDispatch = useDispatch();
  const { aData } = useSelector(selectReportsState);

  const onGenerate = (oData) => oDispatch(actionReportGetTax(oData));

  const [dDateTo, setDateTo] = useState(oDayJs().toDate());
  const [oCurrency, setCurrency] = useState(CURRENCY_VALUES[1]);
  const [oDocumentType, setDocumentType] = useState(SELECTABLE_DOCUMENT_TYPES[2]);
  const [dDateFrom, setDateFrom] = useState(oDayJs().subtract(1, "month").toDate());

  const nTotal = useMemo(() => {
    if (!aData || aData?.length === 0) return 0;
    let total = aData.reduce((nAcc, oItem) => nAcc + oItem.totIva, 0);
    return total.toFixed(4);
  }, [aData]);

  const oUser = useSelector(selectLoguedUser);
  useEffect(() => {
    if (oUser && oUser.config?.idCurrency)
      setCurrency(CURRENCY_VALUES.find(item => item.value === oUser.config?.idCurrency));
  }, [oUser]);

  const onExport = () => {
    onGenerate({
      monid: oCurrency.value,
      tipo: oDocumentType.value,
      hasta: CastDate(dDateTo, DATES_FORMATS.MYSQL_SERVER),
      desde: CastDate(dDateFrom, DATES_FORMATS.MYSQL_SERVER),
    });
  };

  return (
    <section>
      <section className="grid grid-cols-1 sm:grid-cols-2">
        <h1 className="ui-page-title self-center text-center md:text-left">
          Resumen de IVA
        </h1>
        <div className="flex items-center justify-end gap-5">
          {aData?.length > 0 &&
            <CSVLink
              data={aData.map(TaxMapper)}
              className="ml-auto"
              headers={csvHeaders}
              title={`Resumen de IVA ${CastDate(new Date(), DATES_FORMATS.LATIN)}`}
              filename={`Resumen de IVA ${CastDate(new Date(), DATES_FORMATS.LATIN)}`}
            >
              <Button
                label="Exportar"
                type={BUTTON_CONFIG.CLASS.SUCCESS}
                icon={<BiExport className="text-white ml-2 text-lg" />}
              />
            </CSVLink>
          }
          <Button
            label="Generar reporte"
            onClick={onExport}
            type={BUTTON_CONFIG.CLASS.PRIMARY}
            icon={<BiFile className="text-white ml-2 text-lg" />}
          />
        </div>
      </section>
      <section className="grid grid-cols-1 sm:grid-cols-2 ui-card py-4 px-5 mt-4 gap-5">
        <div className="relative col-span-1 gap-5">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400 text-sm">Fecha inicio</span>
          </div>
          <DateInput
            value={dDateFrom}
            onChange={setDateFrom}
            placeholder="Fecha inicio"
            containerClassName="rounded-lg overflow-hidden ml-20"
            className="ui-field w-full"
          />
        </div>
        <div className="relative col-span-1 gap-5">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <span className="text-gray-500 dark:text-gray-400 text-sm">Fecha fin</span>
          </div>
          <DateInput
            value={dDateTo}
            onChange={setDateTo}
            placeholder="Fecha fin"
            containerClassName="rounded-lg overflow-hidden ml-20"
            className="ui-field w-full"
          />
        </div>
        <div className="relative col-span-1 gap-5">
          <ComboBox
            selected={oCurrency}
            onChange={setCurrency}
            label="Moneda"
            emptyLabel="Todas"
            options={[CURRENCY_VALUES[1], CURRENCY_VALUES[2]]}
          />
        </div>
        <div className="relative col-span-1 gap-5">
          <ComboBox
            selected={oDocumentType}
            onChange={setDocumentType}
            label="Tipo de documentos"
            emptyLabel="Tipo de documentos"
            options={SELECTABLE_DOCUMENT_TYPES}
          />
        </div>
      </section>
      <section className="py-5">
        <Table>
          <Table.Header>
            <Table.Row isHeaderOrFooter>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Tipo
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Fecha
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Nro. Interno
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                CAE
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-right">
                Total IVA
              </Table.Col>
              <Table.Col scope="col" className="px-3 py-2 text-center">
                Estado
              </Table.Col>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {aData.length > 0 ? (
              aData.map(TaxMapper).map((oItem, nIndex) => (
                <Table.Row key={`${nIndex}`}>
                  <Table.Col className="px-3 py-2 text-center">
                    {oItem.type}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center">
                    {oItem.date}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center">
                    {oItem.nro}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center">
                    {oItem.cae || "-"}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-right">
                    {oItem.iva}
                  </Table.Col>
                  <Table.Col className="px-3 py-2 text-center">
                    {oItem.status || "-"}
                  </Table.Col>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Col className="px-3 py-2 text-center" colSpan={7}>
                  <em>Sin resultados</em>
                </Table.Col>
              </Table.Row>
            )}
          </Table.Body>
          <Table.Footer>
            <Table.Row isHeaderOrFooter>
              <Table.Col colSpan="4" className="px-3 py-2 text-right">
                <Text>Total IVA:</Text>
              </Table.Col>
              <Table.Col className="px-3 py-2 text-right">
                <Text>{nTotal}</Text>
              </Table.Col>
              <Table.Col className="px-3 py-2 text-right" />
            </Table.Row>
          </Table.Footer>
        </Table>
      </section>
    </section>
  );
};

export default TaxReport;
